/* Present board, from the reference tool, with the fit and print changes in ADR-010. */
.board {
  padding: 34px 40px 80px;
  max-width: 1180px;
  margin: 0 auto;
}
.head {
  margin-bottom: 26px;
}
.head h1 {
  font-size: 30px;
}
.sub {
  color: var(--muted);
  margin-top: 6px;
  font-size: 15px;
}
.eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--blue-text);
}

/* Totals strip: the Quantify totals cards, a little smaller. */
.totals {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin: -8px 0 24px;
}
.total {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 18px;
  min-width: 180px;
}
.total.hero {
  background: var(--blue-d);
  border-color: var(--blue-d);
}
.total.alt {
  background: #eff8f1;
  border-color: #cde8d5;
}
.tK {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted);
}
.hero .tK {
  color: var(--green-on-dark);
}
.tV {
  font-size: 24px;
  font-weight: 900;
  color: var(--ink);
  margin-top: 2px;
  letter-spacing: -0.02em;
}
.hero .tV {
  color: #fff;
}
.alt .tV {
  color: var(--green-text);
}
.tRange {
  font-size: 12px;
  color: #bfe0ee;
  margin-top: 2px;
}

/* auto-fit, not the reference's auto-fill: one or two cards share the full width. */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  gap: 20px;
}
.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px 21px;
  box-shadow: var(--shadow);
  break-inside: avoid;
  container-type: inline-size;
}
.cardHead {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 4px;
}
.cardHead h3 {
  flex: 1;
  font-size: 18px;
}
.rank {
  flex: 0 0 26px;
  height: 26px;
  border-radius: 7px;
  color: #fff;
  font-weight: 900;
  font-size: 13px;
  display: grid;
  place-items: center;
}
.tier {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  flex: 0 0 auto;
  margin-top: 2px;
}
.tier.quick {
  background: #eff8f1;
  color: var(--green-text);
}
.tier.bet {
  background: #e8f3f8;
  color: var(--blue-text);
}
.tier.fill {
  background: var(--bg);
  color: var(--muted);
}
.tier.recon {
  background: #fdf4e7;
  color: var(--amber-text);
}
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.who {
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
}
.stakeholder {
  color: var(--blue-text);
  font-weight: 700;
}
.win {
  color: var(--body);
  margin-top: 3px;
}
.winK {
  color: var(--blue-text);
  font-weight: 700;
}
.whoSpacer {
  height: 6px;
}

/* A wide card (a lone card on screen, or any card in print) reads before and after side by side. */
@container (min-width: 600px) {
  .rows {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 28px;
  }
}

.row {
  margin-bottom: 12px;
  break-inside: avoid;
}
.k {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.v {
  font-size: 14.5px;
  color: var(--body);
  margin-top: 2px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 5px;
}
.vchip {
  font-size: 12px;
  font-weight: 700;
  background: #eaf5ee;
  color: var(--green-text);
  border: 1px solid #cde8d5;
  border-radius: 999px;
  padding: 4px 10px;
}
.m {
  color: var(--body);
  font-weight: 400;
}
.coiLine {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--amber-line);
  font-size: 14px;
  break-inside: avoid;
}
.coiK {
  color: var(--amber-text);
  font-weight: 700;
}
.valLine {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #cde8d5;
  font-size: 14px;
  break-inside: avoid;
}
.valK {
  color: var(--green-text);
  font-weight: 700;
}
.empty {
  color: var(--muted);
  text-align: center;
  padding: 60px;
}
.brand {
  padding-bottom: 12px;
  margin-bottom: 18px;
  border-bottom: 2px solid var(--blue-d);
}
.brand img {
  display: block;
  height: 26px;
  width: auto;
}

@media (max-width: 820px) {
  .board {
    padding: 22px 18px 60px;
  }
}

/* A4, with the map name and date (set on the root element by Board.tsx) and page numbers. */
@page {
  size: A4;
  margin: 14mm 12mm 16mm;
  @bottom-left {
    content: var(--vm-print-footer, "Value Map");
    font-family: Lato, sans-serif;
    font-size: 8pt;
    color: #6b818b;
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: Lato, sans-serif;
    font-size: 8pt;
    color: #6b818b;
  }
}

@media print {
  .board {
    padding: 0;
    max-width: none;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  .head,
  .totals {
    break-after: avoid;
    break-inside: avoid;
  }
  /* One full-width card per row: half-width cards ran taller than a page. */
  .grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .card {
    box-shadow: none;
  }
  /* The first card may split (between rows), so the heading never sits alone on page one. */
  .card:first-child {
    break-inside: auto;
  }
}
